import axios, { AxiosError } from 'axios';
import { ConversionSettings, ConversionResult, ConversionStage } from '@/types/conversion';
import { addAuthHeaders } from './auth';
import i18n from '@/i18n';

let ws: WebSocket | null = null;
let progressCallback: ((stage: ConversionStage, progress: number) => void) | null = null;

function setupWebSocket() {
  if (ws) return;

  // В Docker окружении используем относительный путь через прокси
  const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const wsPath = `${wsProtocol}//${window.location.host}/ws`;
  
  ws = new WebSocket(wsPath);
  
  ws.onmessage = (event) => {
    const data = JSON.parse(event.data);
    if (data.stage && typeof data.progress === 'number' && progressCallback) {
      progressCallback(data.stage, data.progress);
    }
  };

  ws.onerror = (error) => {
    console.error('WebSocket error:', error);
  };

  ws.onclose = () => {
    ws = null;
    setTimeout(setupWebSocket, 5000);
  };
}

class ConversionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConversionError';
  }
}

interface ConvertPresentationResponse {
  fileName: string;
  downloadUrl: string;
}

// Всегда используем относительные пути (через прокси)
const axiosInstance = axios.create({
  baseURL: '',
  headers: {
    'Content-Type': 'application/json',
  }
});

// Добавляем перехватчик для добавления auth headers
axiosInstance.interceptors.request.use(config => {
  return addAuthHeaders(config);
});

interface AsyncJobSubmitResponse {
  jobId: string;
  statusUrl: string;
  downloadUrl: string;
  estimatedTime?: number;
}

interface AsyncJobStatusResponse {
  jobId: string;
  status: 'pending' | 'active' | 'completed' | 'failed' | 'cancelled';
  progress?: {
    stage?: string;
    progress?: number;
  };
  error?: string;
  downloadUrl?: string;
  fileName?: string;
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_DURATION_MS = 30 * 60 * 1000; // 30 minutes

export const api = {
  async convertPresentation(
    file: File,
    settings: ConversionSettings,
    onProgress?: (stage: ConversionStage, progress: number) => void
  ): Promise<ConversionResult> {
    progressCallback = onProgress || null;
    setupWebSocket();

    const formData = new FormData();
    formData.append('file', file);

    // Get language from multiple sources with priority
    const i18nLanguage = i18n.language;
    const documentLanguage = document.documentElement.lang;
    const storageLanguage = localStorage.getItem('i18nextLng');

    // Priority: i18n.language > documentElement.lang > localStorage > default 'en'
    const rawLanguage = i18nLanguage || documentLanguage || storageLanguage || 'en';
    // v2 API supports: en, es, fr, it, pl, pt, uk, zh, ar
    const supportedLanguages = ['en', 'es', 'fr', 'it', 'pl', 'pt', 'uk', 'zh', 'ar'];
    const currentLanguage = supportedLanguages.includes(rawLanguage) ? rawLanguage : 'en';
    formData.append('language', currentLanguage);

    try {
      // Step 1: Submit job to async v2 API
      const submitResponse = await axiosInstance.post<AsyncJobSubmitResponse>('/api/v2/jobs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress?.('uploading', progress);
          }
        },
      });

      const { jobId } = submitResponse.data;

      // Step 2: Poll for job completion
      const startTime = Date.now();

      while (true) {
        if (Date.now() - startTime > MAX_POLL_DURATION_MS) {
          throw new ConversionError('Conversion timed out after 30 minutes');
        }

        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));

        const statusResponse = await axiosInstance.get<AsyncJobStatusResponse>(`/api/v2/jobs/${jobId}`);
        const jobStatus = statusResponse.data;

        // Forward progress from poll response to UI
        if (jobStatus.progress?.stage && typeof jobStatus.progress?.progress === 'number') {
          const stageMap: Record<string, ConversionStage> = {
            video: 'video',
            pdf: 'pdf',
            png: 'images',
            images: 'images',
            scorm: 'scorm',
            zip: 'packaging',
            packaging: 'packaging',
          };
          const mappedStage = stageMap[jobStatus.progress.stage] || 'pdf';
          onProgress?.(mappedStage, jobStatus.progress.progress);
        }

        if (jobStatus.status === 'completed') {
          return {
            fileName: jobStatus.fileName || file.name.replace(/\.[^/.]+$/, '.zip'),
            downloadUrl: `/api/v2/jobs/${jobId}/download`,
            jobId,
          };
        }

        if (jobStatus.status === 'failed') {
          throw new ConversionError(jobStatus.error || 'Conversion failed');
        }

        if (jobStatus.status === 'cancelled') {
          throw new ConversionError('Conversion was cancelled');
        }
      }
    } catch (error) {
      if (error instanceof ConversionError) {
        throw error;
      }
      if (error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError && (error as AxiosError).response?.data) {
        const errorData = (error as AxiosError).response!.data as { error?: string };
        throw new ConversionError(errorData.error || 'Conversion failed');
      }
      throw new ConversionError('Network error occurred');
    }
  },

  async getStatistics() {
    try {
      const response = await axiosInstance.get('/api/statistics');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      throw error;
    }
  },

  async sendFeedback(feedback: { message: string, email: string, name: string }) {
    try {
      // Try Cloudflare Worker first (if configured)
      const workerUrl = process.env.REACT_APP_FEEDBACK_WORKER_URL;
      
      if (workerUrl) {
        console.log('Sending feedback via Cloudflare Worker...');
        const response = await fetch(workerUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(feedback),
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Feedback sent via Cloudflare Worker:', data);
          return data;
        } else {
          console.warn('Cloudflare Worker failed, falling back to backend API');
        }
      }

      // Fallback to backend API
      console.log('Sending feedback via backend API...');
      const response = await axiosInstance.post('/api/feedback', feedback);
      return response.data;
    } catch (error) {
      console.error('Failed to send feedback:', error);
      throw error;
    }
  }
};

export const convertPresentation = async (file: File, settings: ConversionSettings) => {
  const result: ConvertPresentationResponse = await api.convertPresentation(file, settings);
  return result;
};
